import React from "react";
import { isEmpty } from "lodash";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import toast from "react-hot-toast";
import { IoTimeOutline, IoShareSocial } from "react-icons/io5";
import { HiOutlineTicket } from "react-icons/hi";
import { FaLocationDot } from "react-icons/fa6";
import { MdClose } from "react-icons/md";
import { ShareButton } from "@npm_leadtech/share-button";

import { PreviewLayout } from ".";
import { Lazy } from "@/components/Lazy";
import { Chip } from "@/components/Chip";
import { Link } from "@/components/Link";
import { Button } from "@/components/Button";
import { Typography } from "@/components/Typography";

import { QREditorContext } from "@/contexts/QREditorContext";
import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";

import { optimizeCloudinaryImage } from "@/services/CloudinaryService";
import { checkValidURL } from "@/utils/formHelper";
import { IconList } from "../IconList";
import { getFullAddress } from "@/utils/qrEditorHelper";
import { copyToClipboard, isClient } from "@/utils/browser";
import { useTranslation } from "react-i18next";
import { PAGES } from "@/internals/constants/pages";

export default function PreviewCoupon(props: QRProps<"coupon">) {
  const { t } = useTranslation(PAGES.QR_COUPON);

  dayjs.extend(isSameOrBefore);
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const qrEditorContextData = React.useContext<QREditorContextProps<"coupon">>(QREditorContext);
  const data = props?.data || qrEditorContextData?.qrData.data;

  const {
    logoUrl,
    socialNetworks,
    themeColor,
    couponName,
    couponDateTime,
    couponTermsAndConditions,
    couponUrlLink,
    couponUrlText,
    infoCompanyName,
    infoDescription,
    infoHeadline,
    infoSalesBadge,
    locationAddress,
    locationCity,
    locationCountry,
    locationNumeration,
    locationPostalCode,
    locationState,
    infoButtonText,
  } = data;

  const [isFlipped, setIsFlipped] = React.useState(false);
  const hasLogo = !isEmpty(logoUrl?.[0]?.url);

  const isInitPreview = props?.initPreview;
  const buttonColor = themeColor?.buttonColor;
  const fullAddress = !isInitPreview
    ? getFullAddress(
        locationAddress,
        locationNumeration,
        locationCity,
        locationPostalCode,
        locationState,
        locationCountry
      )
    : `${locationAddress}, ${locationPostalCode}, ${locationCity}, ${locationCountry}`;
  const addressMapsURL = `http://maps.google.com/?q=${fullAddress}`;

  const checkValidDate = () => {
    const defaultTimezone = dayjs.tz.guess();
    const couponDate = dayjs.tz(
      couponDateTime?.couponValidDate,
      couponDateTime?.couponTimezone || defaultTimezone
    );
    const currentLocalDate = dayjs().tz(dayjs.tz.guess());

    const isValidDate = currentLocalDate.isSameOrBefore(couponDate, "day");

    if (isValidDate) {
      return (
        <div>
          {couponName && (
            <div
              className="coupon-ticket__side__body__code"
              style={{ borderColor: buttonColor }}
            >
              <Typography
                Tag="p"
                size="l"
                classname="coupon-ticket__side__body__code__text"
              >
                {couponName}
              </Typography>
              <Button
                label="Copy"
                onClick={() => {
                  if (isClient()) {
                    copyToClipboard(couponName);
                    toast.success("Copied!", {
                      id: "myToast",
                      duration: 1500,
                      position: "top-center",
                      icon: "📋",
                    });
                  }
                }}
                type="button"
                buttonColor={buttonColor}
                className="coupon-ticket__side__body__code__button"
              />
            </div>
          )}
          {couponDateTime?.couponValidDate && (
            <Typography
              Tag="p"
              size="s"
              classname="coupon-ticket__side__body__date-description u-spacing-5--top"
            >
              <IoTimeOutline />
              <span>
                {t("code_validation")} {couponDate.format("YYYY/MM/DD")}
              </span>
            </Typography>
          )}
        </div>
      );
    } else {
      return (
        <div>
          <div className="coupon-ticket__side__body__expired">
            <Typography
              Tag="p"
              size="s"
              classname="coupon-ticket__side__body__expired__text"
            >
              <IoTimeOutline />
              <span>
                {t("expired_on")} {couponDate.format("YYYY/MM/DD")}
              </span>
            </Typography>
          </div>
          <Typography
            Tag="p"
            size="xs"
            classname="coupon-ticket__side__body__expired__description u-spacing-5--top"
          >
            {t("expired")}
          </Typography>
        </div>
      );
    }
  };

  return (
    <PreviewLayout
      page="coupon"
      dataQA="preview-coupon-container"
      backgroundColor={themeColor?.backgroundColor}
      includeBrowser
      isInitPreview={isInitPreview}
    >
      <div className="coupon-container">
        <div className="coupon-header">
          <Typography Tag="p">{infoCompanyName}</Typography>
          <ShareButton icon={<IoShareSocial />} />
        </div>
        <div className={`coupon-ticket ${isFlipped ? "is-flipped" : ""}`}>
          <div className="coupon-ticket__side coupon-ticket__side--front">
            {!isEmpty(logoUrl?.[0]?.url) && (
              <div className="coupon-ticket__side__banner">
                <Lazy placeholderClass="coupon-ticket__side__banner--placeholder">
                  <img
                    src={optimizeCloudinaryImage({
                      url: logoUrl[0]?.url as string,
                      transformations: ["f_auto,q_auto,w_1280,h_640,dpr_auto,c_fill"],
                    })}
                    className="coupon-ticket__side__banner--background"
                  />
                </Lazy>
              </div>
            )}
            <div className={`coupon-ticket__side__body ${hasLogo ? "" : "without-logo"}`}>
              {infoSalesBadge && (
                <Chip
                  className="coupon-ticket__side__body__badge"
                  label={infoSalesBadge}
                  type="filled"
                  color="primary"
                  size="medium"
                  icon={{ position: "left", component: <HiOutlineTicket /> }}
                  style={{ backgroundColor: buttonColor }}
                />
              )}
              <div className="coupon-ticket__side__body__info">
                <Typography
                  Tag="p"
                  size="xl"
                  classname="coupon-ticket__side__body__info__headline"
                >
                  {infoHeadline}
                </Typography>
                <Typography
                  Tag="p"
                  size="s"
                  classname="coupon-ticket__side__body__info__description"
                >
                  {infoDescription}
                </Typography>
              </div>
            </div>
            <div className="coupon-ticket__side__cta">
              <Button
                buttonColor={buttonColor}
                small
                label={infoButtonText || "Get Coupon"}
                onClick={() => setIsFlipped(true)}
              />
              {!isFlipped && (
                <>
                  <div
                    className="coupon-ticket__side__cta__bullet coupon-ticket__side__cta__bullet--left"
                    style={{ backgroundColor: themeColor?.backgroundColor }}
                  />
                  <div
                    className="coupon-ticket__side__cta__bullet coupon-ticket__side__cta__bullet--right"
                    style={{ backgroundColor: themeColor?.backgroundColor }}
                  />
                </>
              )}
            </div>
          </div>
          {!isEmpty(socialNetworks) && !isFlipped && (
            <div className="coupon-socialNetworks">
              <IconList
                center
                items={socialNetworks}
              />
            </div>
          )}
          {locationAddress && !isFlipped && isInitPreview === undefined && (
            <div className="coupon-address">
              <FaLocationDot />
              <a
                href={addressMapsURL}
                target="_blank"
                rel="noreferrer"
              >
                <Typography
                  Tag="p"
                  size="xs"
                >
                  {fullAddress}
                </Typography>
              </a>
            </div>
          )}
          <div className="coupon-ticket__side coupon-ticket__side--back">
            <div className="coupon-ticket__side__banner">
              <Typography
                Tag="p"
                size="xs"
                classname="coupon-ticket__side__banner__title"
              >
                {t("promotional_code")}
              </Typography>
              <span
                className="coupon-ticket__side__banner__button"
                onClick={() => setIsFlipped(false)}
              >
                <MdClose />
              </span>
            </div>
            <div className="coupon-ticket__side__body">{checkValidDate()}</div>
            <div className="coupon-ticket__side__cta">
              {isFlipped && (
                <>
                  <div
                    className="coupon-ticket__side__cta__bullet coupon-ticket__side__cta__bullet--left"
                    style={{ backgroundColor: themeColor?.backgroundColor }}
                  />
                  <div
                    className="coupon-ticket__side__cta__bullet coupon-ticket__side__cta__bullet--right"
                    style={{ backgroundColor: themeColor?.backgroundColor }}
                  />
                </>
              )}
              {couponUrlLink && (
                <Link
                  className="button u-spacing-2 button--square button--small"
                  href={checkValidURL(couponUrlLink) || ""}
                  style={{
                    color: buttonColor,
                    borderColor: buttonColor,
                  }}
                  externalLink
                  type="button"
                  prefetch={false}
                >
                  <Typography
                    Tag="p"
                    size="s"
                  >
                    {couponUrlText}
                  </Typography>
                </Link>
              )}
            </div>
          </div>
        </div>
        {couponTermsAndConditions && isFlipped && (
          <div className="coupon-terms-and-conditions">
            <Typography
              Tag="h3"
              size="xs"
            >
              {t("terms_and_conditions")}
            </Typography>
            <Typography
              Tag="p"
              size="xs"
            >
              {couponTermsAndConditions}
            </Typography>
          </div>
        )}
        {isInitPreview && (
          <div className="initPreview-logos">
            {data?.socialLogos?.map((socialLogo) => (
              <Lazy
                key={socialLogo}
                placeholderClass="skeleton-placeholder__website__logo"
              >
                <img
                  key={socialLogo}
                  src={socialLogo}
                  alt="social media logo"
                />
              </Lazy>
            ))}
          </div>
        )}
        {!isEmpty(socialNetworks) && isFlipped && (
          <div className="coupon-socialNetworks">
            <IconList
              center
              items={socialNetworks}
            />
          </div>
        )}
        {locationAddress && (isFlipped || isInitPreview) && (
          <div className="coupon-address">
            <FaLocationDot />
            <a
              href={addressMapsURL}
              target="_blank"
              rel="noreferrer"
            >
              <Typography
                Tag="p"
                size="xs"
              >
                {fullAddress}
              </Typography>
            </a>
          </div>
        )}
      </div>
    </PreviewLayout>
  );
}
